import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import SchemaPreviewDialog from './common/SchemaPreviewDialog';
import SchemaDetailDrawer from './common/SchemaDetailDrawer';
import GuidDisplay from './common/GuidDisplay';
import { useSchemaQueries, useConnectorQueries } from './schema-manager/useSchemaSetQueries';
import { useSchemaMutations } from './schema-manager/useSchemaSetMutations';
import SchemaSetTable from './schema-manager/SchemaSetTable';
import SchemaDraftTable from './schema-manager/SchemaDraftTable';
import SchemaDraftDetailView from './schema-manager/SchemaDraftDetailView';
import CreateSchemaSetDialog from './schema-manager/CreateSchemaSetDialog';
import CloneSchemaSetDialog from './schema-manager/CloneSchemaSetDialog';
import DeleteSchemaSetDialog from './schema-manager/DeleteSchemaSetDialog';
import AnnotateDraftDialog from './schema-manager/AnnotateDraftDialog';
import UploadSchemaDialog from './schema-manager/UploadSchemaDialog';
import EditDraftDialog from './schema-manager/EditDraftDialog';
import ValidationRulesDialog from './schema-manager/ValidationRulesDialog';
import PromoteDraftDialog from './schema-manager/PromoteDraftDialog';

const blinkAnimation = {
  animation: 'pageFlash 450ms ease-in-out',
  '@keyframes pageFlash': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 },
  },
};

const SchemaSetManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [blinkKey, setBlinkKey] = useState(0);

  const queries = useSchemaQueries();
  const mutations = useSchemaMutations(queries.listParams);
  const { usableConnectors } = useConnectorQueries(mutations.uploadDialogOpen, mutations.uploadMode);

  const handleUploadSubmit = (opts?: { selectedSheets?: string[]; selectedColumnsBySheet?: Record<string, string[]>; fileType?: 'csv' | 'excel' | 'sql'; content?: string }) => {
    const selectedSheets = opts?.selectedSheets;
    const selectedColumnsBySheet = opts?.selectedColumnsBySheet;
    const content = opts?.content ?? mutations.uploadContent;

    let sourceType: 'sql_ddl' | 'json_template' | 'file_upload' | 'csv_template' | 'excel_template' | 'template';
    if (mutations.uploadMode === 'sql') {
      sourceType = 'sql_ddl';
    } else if (mutations.uploadMode === 'json') {
      sourceType = 'json_template';
    } else if (mutations.uploadMode === 'template') {
      sourceType = 'template';
    } else if (mutations.uploadMode === 'file' && (opts?.fileType === 'csv' || mutations.uploadFileType === 'csv')) {
      sourceType = 'csv_template';
    } else if (mutations.uploadMode === 'file' && (opts?.fileType === 'excel' || mutations.uploadFileType === 'excel')) {
      sourceType = 'excel_template';
    } else if (mutations.uploadMode === 'file' && (opts?.fileType === 'sql' || mutations.uploadFileType === 'sql')) {
      sourceType = 'sql_ddl';
    } else {
      sourceType = 'file_upload';
    }
    mutations.uploadMutation.mutate({
      content,
      sourceType,
      schemaName: mutations.uploadSchemaName || undefined,
      additionalFiles: sourceType === 'template' && Object.keys(mutations.templateAdditionalFiles).length > 0
        ? mutations.templateAdditionalFiles
        : undefined,
      selectedSheets: sourceType === 'excel_template' && selectedSheets && selectedSheets.length > 0
        ? selectedSheets
        : undefined,
      selectedColumnsBySheet: (sourceType === 'excel_template' || sourceType === 'csv_template') ? selectedColumnsBySheet : undefined,
    });
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setBlinkKey((k) => k + 1);

    try {
      await Promise.all([
        queries.schemasQuery.refetch(),
        queries.draftsQuery.refetch(),
      ]);
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 450);
    }
  };

  const handleExtractSubmit = () => {
    mutations.extractConnectorMutation.mutate({
      connectorId: mutations.selectedConnectorId,
      tableNames: mutations.selectedTablesForExtract.length > 0 ? mutations.selectedTablesForExtract : undefined,
      schemaName: mutations.uploadSchemaName || undefined,
      createdBy: 'web-ui',
    });
  };

  const handleSaveDraft = (definition: any) => {
    if (mutations.selectedDraft) {
      mutations.updateDraftMutation.mutate({ schemaId: mutations.selectedDraft.schema_id, definition });
    }
  };

  // State for delete dialog
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [dependencyInfo, setDependencyInfo] = useState<{
    hasDependencies: boolean;
    dependencies: Record<string, any>;
    totalCount: number;
  } | undefined>();

  const handleDeleteConfirm = () => {
    if (!mutations.deleteDialogOpen.schema) return;

    setDeleteError(undefined);
    setDependencyInfo(undefined);
    mutations.deleteMutation.mutate(mutations.deleteDialogOpen.schema.schema_id, {
      onSuccess: (response) => {
        if (response?.success === false) {
          setDeleteError(response.error);
          if (response.dependencies && response.total_count) {
            setDependencyInfo({
              hasDependencies: true,
              dependencies: response.dependencies,
              totalCount: response.total_count
            });
          }
        }
      }
    });
  };

  return (
    <Box
      key={blinkKey}
      sx={{
        ...(isRefreshing ? blinkAnimation : {}),
        animationFillMode: 'both',
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <StorageIcon color="primary" />
        <Typography variant="h4">Target Schemas</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={handleRefresh}
              disabled={queries.schemasQuery.isFetching || isRefreshing}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Tab Navigation */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          if (v === 0) {
            queries.schemasQuery.refetch();
          } else {
            queries.draftsQuery.refetch();
          }
        }}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Schemas" />
        <Tab label="Schema Drafts" />
      </Tabs>

      {/* Schemas Tab */}
      {activeTab === 0 && (
        <SchemaSetTable
          schemaSets={queries.schemas}
          isLoading={queries.schemasQuery.isLoading}
          isError={queries.schemasQuery.isError}
          error={queries.schemasQuery.error}
          searchInput={queries.searchInput}
          onSearchInputChange={queries.setSearchInput}
          onCommitSearch={queries.commitSearch}
          statusFilter={queries.statusFilter}
          onStatusFilterChange={queries.setStatusFilter}
          onOpenCreate={mutations.handleOpenCreate}
          onViewDetails={mutations.setSelectedSchema}
          onClone={mutations.handleOpenClone}
          onDelete={mutations.handleOpenDelete}
        />
      )}

      {/* Schema Drafts Tab */}
      {activeTab === 1 && (
        <SchemaDraftTable
          drafts={queries.drafts}
          isLoading={queries.isLoadingDrafts}
          onViewDetails={mutations.setSelectedDraft}
          onEdit={mutations.handleEditDraft}
          onAnnotate={(schema) => mutations.handleAnnotate(schema)}
          onPromote={mutations.handlePromote}
          onDelete={mutations.handleOpenDelete}
          onUpload={() => mutations.setUploadDialogOpen(true)}
        />
      )}

      {/* Schema Details Dialog */}
      <SchemaPreviewDialog
        open={Boolean(mutations.selectedSchema)}
        schema={mutations.selectedSchema}
        onClose={() => mutations.setSelectedSchema(null)}
      />

      {/* Draft Details Drawer */}
      <SchemaDetailDrawer
        open={Boolean(mutations.selectedDraft) && !mutations.annotateDialogOpen && !mutations.promoteDialogOpen}
        onClose={() => mutations.setSelectedDraft(null)}
        title={
          mutations.selectedDraft && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} noWrap>
                Schema Details
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                <GuidDisplay guid={mutations.selectedDraft.schema_id} />
              </Typography>
            </Stack>
          )
        }
      >
        {mutations.selectedDraft && (
          <SchemaDraftDetailView
            draft={mutations.selectedDraft}
            onClose={() => mutations.setSelectedDraft(null)}
            onAnnotate={(mode) => mutations.handleAnnotate(mutations.selectedDraft!, mode)}
            onPromote={() => mutations.handlePromote(mutations.selectedDraft!)}
            onEdit={() => mutations.handleEditDraft(mutations.selectedDraft!)}
          />
        )}
      </SchemaDetailDrawer>

      {/* Edit Draft Dialog */}
      <EditDraftDialog
        open={mutations.editDialogOpen}
        onClose={() => mutations.setEditDialogOpen(false)}
        selectedDraft={mutations.selectedDraft}
        editMode={mutations.editMode}
        onEditModeChange={mutations.setEditMode}
        editPayload={mutations.editPayload}
        onEditPayloadChange={mutations.setEditPayload}
        editPayloadText={mutations.editPayloadText}
        onEditPayloadTextChange={mutations.setEditPayloadText}
        editError={mutations.editError}
        onEditErrorChange={mutations.setEditError}
        expandedEditTables={mutations.expandedEditTables}
        onExpandedEditTablesChange={mutations.setExpandedEditTables}
        onSave={handleSaveDraft}
        isSaving={mutations.updateDraftMutation.isLoading}
        onOpenRulesEditor={(tableName, columnName) => {
          mutations.setRulesEditorContext({ tableName, columnName });
          mutations.setRulesEditorOpen(true);
        }}
      />

      {/* Validation Rules Editor Dialog */}
      <ValidationRulesDialog
        open={mutations.rulesEditorOpen}
        onClose={() => mutations.setRulesEditorOpen(false)}
        rulesEditorContext={mutations.rulesEditorContext}
        editPayload={mutations.editPayload}
        onEditPayloadChange={mutations.setEditPayload}
      />

      {/* Annotate Dialog (unified — replaces the old Enrich + Interpret dialogs) */}
      <AnnotateDraftDialog
        open={mutations.annotateDialogOpen}
        onClose={() => mutations.setAnnotateDialogOpen(false)}
        selectedDraft={mutations.selectedDraft}
        form={mutations.annotateForm}
        onFormChange={mutations.setAnnotateForm}
        onSubmit={() =>
          mutations.selectedDraft &&
          mutations.annotateMutation.mutate({
            schemaId: mutations.selectedDraft.schema_id,
            mode: mutations.annotateForm.mode,
            guidance: mutations.annotateForm.guidance || undefined,
            instructions: mutations.annotateForm.instructions || undefined,
            includeTemplateRules: mutations.annotateForm.includeTemplateRules,
          })
        }
        isLoading={mutations.annotateMutation.isLoading}
      />

      {/* Upload Dialog */}
      <UploadSchemaDialog
        open={mutations.uploadDialogOpen}
        onClose={() => {
          mutations.setUploadDialogOpen(false);
          mutations.setUploadMode('sql');
          mutations.setUploadContent('');
          mutations.setUploadSchemaName('');
          mutations.setUploadFiles([]);
          mutations.setUploadFileType('text');
          mutations.setTemplateAdditionalFiles({});
          mutations.setSelectedConnectorId('');
          mutations.setAvailableTablesForExtract([]);
          mutations.setSelectedTablesForExtract([]);
        }}
        uploadMode={mutations.uploadMode}
        onUploadModeChange={mutations.setUploadMode}
        uploadContent={mutations.uploadContent}
        onUploadContentChange={mutations.setUploadContent}
        uploadSchemaName={mutations.uploadSchemaName}
        onUploadSchemaNameChange={mutations.setUploadSchemaName}
        uploadFiles={mutations.uploadFiles}
        onUploadFilesChange={mutations.setUploadFiles}
        uploadFileType={mutations.uploadFileType}
        onUploadFileTypeChange={mutations.setUploadFileType}
        selectedConnectorId={mutations.selectedConnectorId}
        onSelectedConnectorIdChange={mutations.setSelectedConnectorId}
        availableTablesForExtract={mutations.availableTablesForExtract}
        onAvailableTablesChange={mutations.setAvailableTablesForExtract}
        selectedTablesForExtract={mutations.selectedTablesForExtract}
        onSelectedTablesChange={mutations.setSelectedTablesForExtract}
        discoverLoading={mutations.discoverLoading}
        onDiscoverLoadingChange={mutations.setDiscoverLoading}
        templateAdditionalFiles={mutations.templateAdditionalFiles}
        onTemplateAdditionalFilesChange={mutations.setTemplateAdditionalFiles}
        usableConnectors={usableConnectors}
        onUpload={handleUploadSubmit}
        onExtract={handleExtractSubmit}
        isUploading={mutations.uploadMutation.isLoading}
        isExtracting={mutations.extractConnectorMutation.isLoading}
        onSnackbar={mutations.setSnackbar}
      />

      {/* Promote Dialog */}
      <PromoteDraftDialog
        open={mutations.promoteDialogOpen}
        onClose={() => mutations.setPromoteDialogOpen(false)}
        selectedDraft={mutations.selectedDraft}
        schemas={queries.schemas}
        promoteForm={mutations.promoteForm}
        onPromoteFormChange={mutations.setPromoteForm}
        promoteCreateNew={mutations.promoteCreateNew}
        onPromoteCreateNewChange={mutations.setPromoteCreateNew}
        promoteNewSchemaName={mutations.promoteNewSchemaName}
        onPromoteNewSchemaNameChange={mutations.setPromoteNewSchemaName}
        promoteCreating={mutations.promoteCreating}
        onPromoteCreatingChange={mutations.setPromoteCreating}
        onPromote={(schemaId, payload) => mutations.promoteMutation.mutate({ schemaId, payload })}
        isPromoting={mutations.promoteMutation.isLoading}
        onSnackbar={mutations.setSnackbar}
      />

      {/* Create Schema Dialog */}
      <CreateSchemaSetDialog
        open={mutations.createDialogOpen}
        onClose={() => mutations.setCreateDialogOpen(false)}
        createForm={mutations.createForm}
        onFormChange={mutations.setCreateForm}
        onSubmit={() => mutations.createMutation.mutate()}
        isLoading={mutations.createMutation.isLoading}
        existingSchemas={queries.schemas}
      />

      {/* Clone Schema Dialog */}
      <CloneSchemaSetDialog
        open={mutations.cloneDialogOpen.open}
        schema={mutations.cloneDialogOpen.schema}
        onClose={() => mutations.setCloneDialogOpen({ open: false, schema: null })}
        cloneForm={mutations.cloneForm}
        onFormChange={mutations.setCloneForm}
        onSubmit={() =>
          mutations.cloneDialogOpen.schema &&
          mutations.cloneMutation.mutate(mutations.cloneDialogOpen.schema.schema_id)
        }
        isLoading={mutations.cloneMutation.isLoading}
      />

      {/* Delete Schema Dialog */}
      <DeleteSchemaSetDialog
        open={mutations.deleteDialogOpen.open}
        schema={mutations.deleteDialogOpen.schema}
        onClose={() => {
          mutations.setDeleteDialogOpen({ open: false, schema: null });
          setDeleteError(undefined);
          setDependencyInfo(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={mutations.deleteMutation.isLoading}
        deleteError={deleteError}
        dependencyInfo={dependencyInfo}
      />

      {/* Snackbar */}
      <Snackbar
        open={Boolean(mutations.snackbar)}
        autoHideDuration={4000}
        onClose={() => mutations.setSnackbar(null)}
        message={mutations.snackbar?.message}
      />
    </Box>
  );
};

export default SchemaSetManager;
